import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Search, Heart, MessageSquare, User, Menu, X, LogOut, Plus, Building, CreditCard, Key } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

export const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = (role = 'renter') => {
    const redirectUrl = window.location.origin + '/auth/callback?role=' + role;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo - Primary Brand Element - DOMINANT visual anchor */}
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <img 
              src="/staypairhomes-logo.png" 
              alt="Stay Pair Homes" 
              className="h-20 sm:h-24 md:h-28 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/browse" 
              className="text-slate-600 hover:text-trust-blue font-medium transition-colors flex items-center gap-2"
              data-testid="nav-browse"
            >
              <Search className="w-4 h-4" />
              Browse
            </Link>
            
            {isAuthenticated && (
              <>
                <Link 
                  to="/saved" 
                  className="text-slate-600 hover:text-trust-blue font-medium transition-colors flex items-center gap-2"
                  data-testid="nav-saved"
                >
                  <Heart className="w-4 h-4" />
                  Saved
                </Link>
                <Link 
                  to="/messages" 
                  className="text-slate-600 hover:text-trust-blue font-medium transition-colors flex items-center gap-2"
                  data-testid="nav-messages"
                >
                  <MessageSquare className="w-4 h-4" />
                  Messages
                </Link>
              </>
            )}
          </div>

          {/* Right Side */}
          <div className="hidden md:flex items-center gap-4">
            {/* Admin Key Icon */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link 
                    to="/admin" 
                    className="p-2 text-slate-400 hover:text-trust-blue hover:bg-slate-100 rounded-lg transition-colors"
                    aria-label="Admin"
                    data-testid="admin-key-icon"
                  >
                    <Key className="w-5 h-5" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Admin Panel</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {isAuthenticated ? (
              <>
                {user?.role === 'owner' && (
                  <Link to="/create-listing">
                    <Button 
                      className="bg-coral hover:bg-coral-hover text-white rounded-full"
                      data-testid="create-listing-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      List Property
                    </Button>
                  </Link>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 hover:bg-slate-50 rounded-full p-1 pr-3 transition-colors" data-testid="user-menu-trigger">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={user?.picture} alt={user?.name} />
                        <AvatarFallback className="bg-trust-blue text-white">
                          {user?.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium text-slate-700">{user?.name?.split(' ')[0]}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2" data-testid="menu-dashboard">
                        <Building className="w-4 h-4" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2" data-testid="menu-profile">
                        <User className="w-4 h-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/payment/settings" className="flex items-center gap-2" data-testid="menu-payments">
                        <CreditCard className="w-4 h-4" />
                        Payment Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-600" data-testid="login-btn">
                    Log in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-trust-blue hover:bg-trust-blue-hover text-white rounded-full" data-testid="signup-btn">
                    Sign up
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 py-4 px-4">
          <div className="flex flex-col gap-4">
            <Link to="/browse" className="flex items-center gap-3 text-slate-700 py-2" onClick={() => setMobileMenuOpen(false)}>
              <Search className="w-5 h-5" />
              Browse Properties
            </Link>
            {isAuthenticated ? (
              <>
                <Link to="/saved" className="flex items-center gap-3 text-slate-700 py-2" onClick={() => setMobileMenuOpen(false)}>
                  <Heart className="w-5 h-5" />
                  Saved
                </Link>
                <Link to="/messages" className="flex items-center gap-3 text-slate-700 py-2" onClick={() => setMobileMenuOpen(false)}>
                  <MessageSquare className="w-5 h-5" />
                  Messages
                </Link>
                <Link to="/dashboard" className="flex items-center gap-3 text-slate-700 py-2" onClick={() => setMobileMenuOpen(false)}>
                  <Building className="w-5 h-5" />
                  Dashboard
                </Link>
                {user?.role === 'owner' && (
                  <Link to="/create-listing" className="flex items-center gap-3 text-coral py-2" onClick={() => setMobileMenuOpen(false)}>
                    <Plus className="w-5 h-5" />
                    List Property
                  </Link>
                )}
                <button onClick={handleLogout} className="flex items-center gap-3 text-red-600 py-2">
                  <LogOut className="w-5 h-5" />
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="flex items-center gap-3 text-slate-700 py-2" onClick={() => setMobileMenuOpen(false)}>
                  <User className="w-5 h-5" />
                  Log in
                </Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-trust-blue hover:bg-trust-blue-hover text-white rounded-full">
                    Sign up
                  </Button>
                </Link>
              </>
            )}
            
            {/* Admin Access - Always visible */}
            <div className="border-t border-slate-200 mt-4 pt-4">
              <Link 
                to="/admin" 
                className="flex items-center gap-3 text-slate-500 py-2" 
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-admin-link"
              >
                <Key className="w-5 h-5" />
                Admin Access
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <Link to="/" className="inline-block mb-4">
              <img 
                src="/staypairhomes-logo.png" 
                alt="Stay Pair Homes" 
                className="h-10 w-auto brightness-0 invert"
              />
            </Link>
            <p className="text-slate-400 text-sm mb-3">
              Connecting property owners with renters for flexible, mid-term housing in Los Angeles County.
            </p>
            <a 
              href="https://staypairhomes.com" 
              className="text-trust-blue hover:text-blue-400 text-sm font-medium transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              staypairhomes.com
            </a>
          </div>
          
          <div>
            <h4 className="font-heading font-semibold mb-4">For Renters</h4>
            <ul className="space-y-2 text-slate-400">
              <li><Link to="/browse" className="hover:text-white transition-colors">Browse Listings</Link></li>
              <li><Link to="/how-it-works" className="hover:text-white transition-colors">How It Works</Link></li>
              <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-heading font-semibold mb-4">For Owners</h4>
            <ul className="space-y-2 text-slate-400">
              <li><Link to="/register?role=owner" className="hover:text-white transition-colors">List Your Property</Link></li>
              <li><Link to="/owner-guide" className="hover:text-white transition-colors">Owner Guide</Link></li>
              <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-heading font-semibold mb-4">Support</h4>
            <ul className="space-y-2 text-slate-400">
              <li><Link to="/help" className="hover:text-white transition-colors">Help Center</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
              <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} Stay Pair Homes. All rights reserved.</p>
          <p className="mt-2">Currently serving Los Angeles County, California.</p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children, showFooter = true }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      {showFooter && <Footer />}
    </div>
  );
};

export default Layout;
